import {
    createContext,
    type PropsWithChildren,
    useContext,
    useMemo,
    useState,
} from 'react';

import { isTourSeen, markTourSeen } from '@/shared/lib/tour';
import type { WelcomeModalConfig } from '@/shared/types';

export interface ModalState {
    closeHandler: () => void;
    config: WelcomeModalConfig;
    isOpen: boolean;
    startHandler: () => void;
}

interface OpenWelcomeModalParams {
    config: WelcomeModalConfig;
    pagePath: string;
    version: string;
    startHandler: () => void;
    closeHandler: () => void;
}

interface TourContextValue {
    modal: ModalState | null;
    openWelcomeModal: (() => void) | null;
    openWelcomeModalAuto: (params: OpenWelcomeModalParams) => void;
    setModal: (modal: ModalState | null) => void;
    setOpenWelcomeModal: (callback: (() => void) | null) => void;
}

const TourContext = createContext<TourContextValue | undefined>(undefined);

export function TourContextProvider(props: PropsWithChildren) {
    const { children } = props;

    const [modal, setModal] = useState<ModalState | null>(null);
    const [openWelcomeModal, setOpenWelcomeModal] = useState<
        (() => void) | null
    >(null);

    const openWelcomeModalAuto = useMemo(
        () => (params: OpenWelcomeModalParams) => {
            const { config, pagePath, version, startHandler, closeHandler } =
                params;

            if (isTourSeen(pagePath, version)) {
                return;
            }

            markTourSeen(pagePath, version);

            setModal({
                config,
                isOpen: true,
                startHandler: () => {
                    setModal(null);

                    setTimeout(() => {
                        startHandler();
                    }, 300);
                },
                closeHandler: () => {
                    setModal(null);
                    closeHandler();
                },
            });
        },
        [],
    );

    const value = useMemo(
        () => ({
            modal,
            openWelcomeModal,
            openWelcomeModalAuto,
            setModal,
            setOpenWelcomeModal,
        }),
        [modal, openWelcomeModal, openWelcomeModalAuto],
    );

    return (
        <TourContext.Provider value={value}>{children}</TourContext.Provider>
    );
}

export function useTourContext(): TourContextValue {
    const context = useContext(TourContext);

    if (!context) {
        throw new Error(
            'useTourContext must be used within TourContextProvider',
        );
    }

    return context;
}
